import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const DISMISSED_KEY = '9meals_pwa_install_dismissed';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOSSafari() {
  const ua = navigator.userAgent;
  return /iP(hone|ad|od)/.test(ua) && /WebKit/.test(ua) && !/(CriOS|FxiOS|OPiOS|EdgiOS)/.test(ua);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
}

function InstallPrompt() {
  const { t } = useTranslation('common');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_COOLDOWN_MS) return;

    // iOS Safari — no beforeinstallprompt, show manual instruction
    if (isIOSSafari()) {
      setIsIOS(true);
      setShow(true);
      return;
    }

    // Android / Desktop Chrome — capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  }, []);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-[9998] mx-auto max-w-md rounded-2xl bg-[#0D0C07] p-4 shadow-xl"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <img
          src="/web-app-manifest-192x192.png"
          alt=""
          className="h-10 w-10 flex-shrink-0 rounded-lg"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">
            {t('installPrompt.title', 'Install 9 Meals')}
          </p>
          <p className="mt-0.5 text-xs text-[#a9a8a3]">
            {isIOS
              ? t('installPrompt.iosDescription', 'Tap the share button ⬆ then "Add to Home Screen"')
              : t('installPrompt.description', 'Add to your home screen for a faster experience')}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleDismiss}
          className={`rounded-lg border border-[#333] px-3 py-2 text-sm font-medium text-[#a9a8a3] transition-colors hover:border-[#555] ${isIOS ? 'flex-1' : 'flex-1'}`}
        >
          {t('installPrompt.dismiss', 'Not now')}
        </button>
        {!isIOS && (
          <button
            onClick={handleInstall}
            className="flex-1 rounded-lg bg-[#FFCB00] px-3 py-2 text-sm font-semibold text-[#0D0C07] transition-colors hover:bg-[#e6b800]"
          >
            {t('installPrompt.install', 'Install')}
          </button>
        )}
      </div>
    </div>
  );
}

export default InstallPrompt;
